import { TerminalsModel } from "../models/terminals-model";
import { VoyagePlan } from "../models/vessel-planning-model";
import * as Converters from '../../src/converters/converters';
import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { PlanSource } from "../models/voyage-plans-model";
import { VoyageRoute } from "../models/voyage-routing-model";
import { WeatherData } from "../../local_modules/vessel-performance/src/vessel-performance-main";
import { SpotWeather } from "./route-weather-worker";
import { TimeseriesData, RouteSeriesNames, WeatherSeries } from "../../local_modules/voy-timeseries-chart/src/echartUtils";
import { Format } from "../converters/types/converters.types";
import { mpsToKnots } from "./helpers";
import { VesselPositionData } from "../services/types/position-service.types";

export function saveFile(name: string, mime: string, str: string)
{
    var blob = new Blob([str], {type: mime});

    console.log(`%c saving: ${name}`);

    const exportElement = document.createElement('a');
    exportElement.download = name;
    exportElement.href = URL.createObjectURL(blob);
    exportElement.click();
}

export function getDepartureArrivalCityFileName(plan: VoyagePlan): string
{
    // Assume at-sea if we don't have a terminal code
    let departureName = "At-Sea";
    let arrivalName = "At-Sea";

    if (plan.departureTerminalCode)
    {
        departureName = TerminalsModel!.getCity(plan.departureTerminalCode) || plan.departureTerminalCode;
    }

    if (plan.arrivalTerminalCode)
    {
        arrivalName = TerminalsModel!.getCity(plan.arrivalTerminalCode) || plan.departureTerminalCode;
    }

    return `${departureName}-${arrivalName}`;
}

export function downloadPlanWithSelectedRoute(plan: VoyagePlan, format: Format)
{
    const route = plan.routes[plan.selectedRouteIndex];
    if (!route) return;

    try
    {
        // Note: The browser will replace all ':' with '_' when saving the file; So do this explicitly here so we can count on the filename
        const filename = `${getDepartureArrivalCityFileName(plan)}-${route.creationTime}`.replaceAll(':', '_');
        const exported = Converters.exportPlan(plan, format, filename);
        if(exported.data) {
            saveFile(`${filename}.${exported.fileExt}`, exported.mimeType, exported.data);
        }  
    }
    catch (err)
    {
        VoyageDialogPopupManager.createDialog({
            headerText: "Exporting route failed",
            bodyText: "Error occurred when attempting to export route",
            isError: true,
            errorDetails: err instanceof Error ? err.message : <string>err
        });
    }     
}

export function getFormattedDataSource(dataSource: string) {
    const srcMap = new Map([
        ['spire', 'Spire'],
        ['mt','MarineTraffic'],
        ['fbb','FBB'],
        ['inmarsat','Inmarsat'],
        ['sc','StarConnect'],
        ['default', '--']
    ])
    return srcMap.get(dataSource) ?? "--";
}

export function getAisStatusLabel(aisStatus?: number | null): string {
    if (aisStatus === undefined || aisStatus === null) {
        return "--"
    }

    const NavStatusMap = new Map([
        [0, 'Under way using engine'],
        [1, 'At anchor'],
        [2, 'Not under command'],
        [3, 'Restricted maneuverability'],
        [4, 'Constrained by her draught'],
        [5, 'Moored'],
        [6, 'Aground'],
        [7, 'Engaged in fishing'],
        [8, 'Underway sailing'],
    ])
    return NavStatusMap.get(aisStatus) ?? "--";
}

export function speedTooltipText(plan: VoyagePlan) {
    switch (plan?.source) {
        case PlanSource.PASSAGE_PLAN:
            return "Planned speed for this vessel is manually entered by bridge crew for entire voyage between pilot points"
        case PlanSource.ECOVOYAGE:
        case PlanSource.EXCEL_PLAN:
        case PlanSource.VOYAGE_PLANNING:
            return "Planned speed for this vessel is calculated per leg based on vessel type, weather and vessels power setting"
        default:
            return "Plan and planned speed for this vessel is currently not available"
    }
}

export function isPromise(p: Promise<any>, context: any) {
    if (typeof p === 'object' && typeof p.then === 'function') {
      return true;
    }
  
    return false;
}

export enum UserOS {
    WINDOWS = "windows",
    MAC = "mac",
    LINUX = "linux",
    UNIX = "unix",
    UNKNOWN = "unknown"
}

export function getOS() {
    let OSName = UserOS.UNKNOWN;
    let navApp = navigator.userAgent.toLowerCase();
    switch (true) {
      case (navApp.indexOf("win") != -1):
        OSName = UserOS.WINDOWS;
        break;
      case (navApp.indexOf("mac") != -1):
        OSName = UserOS.MAC;
        break;
      case (navApp.indexOf("linux") != -1):
        OSName = UserOS.LINUX;
        break;
      case (navApp.indexOf("x11") != -1):
        OSName = UserOS.UNIX;
        break;
    }
    return OSName;
}


export function createTimeseriesTemplate(name: string, color: string): TimeseriesData {
    return {
        name: name,
        color: color,
        timeseries: {
            values: [],
            timestamps: [],
            direction: [],
        },
    }
}

export function extractSpotWeatherDataFromRoute(routes: VoyageRoute[], mode: string): WeatherData {
    const updatedWeatherData: WeatherData = {
        wind50M: [],
        currents: [],
        wave: [],
        swell: []
    }

    // all routes
    for (let i = 0; i < routes.length; i++) {
        let color = "";
        const userRouteNumber = parseInt(routes[i].uiType![routes[i].uiType!.length-1]);
        if (mode === "planning") {
            color = `--route${userRouteNumber+1}-color`;
        } else {
            color = "--performance-viewer-actual-color";
        }

        let name = "forecasted";
        if (mode === "planning") {
            name = <RouteSeriesNames>`Route ${userRouteNumber+1}`;
        }

        const weatherSeries: WeatherSeries = {
            wind50M: createTimeseriesTemplate(name, color),
            swell: createTimeseriesTemplate(name, color),
            wave: createTimeseriesTemplate(name, color),
            currents: createTimeseriesTemplate(name, color)
        }

        const now = Date.now();
        for (const feature of routes[i].route_geometry) {
            if (feature.geometry.type === "Point") continue;
            
            if (!feature.properties.spotWeathers || !feature.properties.spotWeathers.length) continue;

            for (const spot of <SpotWeather[]>(feature.properties.spotWeathers)) {
                //! Todo: until we get latest forecast version for all timestamps,
                //! historical weather data should be skipped. Remove this 'continue'
                //! when fixed
                if (new Date(spot.timestamp).getTime() < now) {
                    continue;
                }
                if (spot.weather.Wind50M) {
                    weatherSeries.wind50M.timeseries.timestamps.push(new Date(spot.timestamp).getTime());
                    weatherSeries.wind50M.timeseries.direction!.push(spot.weather.Wind50M.direction);
                    weatherSeries.wind50M.timeseries.values.push(parseFloat(mpsToKnots(spot.weather.Wind50M.value).toFixed(1)));
                }
                if (spot.weather.Swell) {
                    weatherSeries.swell.timeseries.timestamps.push(new Date(spot.timestamp).getTime());
                    weatherSeries.swell.timeseries.direction!.push(spot.weather.Swell.direction);
                    weatherSeries.swell.timeseries.values.push(parseFloat(spot.weather.Swell.value.toFixed(1)));
                }
                if (spot.weather.Wave) {
                    weatherSeries.wave.timeseries.timestamps.push(new Date(spot.timestamp).getTime());
                    weatherSeries.wave.timeseries.direction!.push(spot.weather.Wave.direction);
                    weatherSeries.wave.timeseries.values.push(parseFloat(spot.weather.Wave.value.toFixed(1)));
                }
                if (spot.weather.Currents) {
                    let direction = spot.weather.Currents.direction;
                    if (direction) {
                        if (direction > 180) {
                            direction -= 180;
                        } else {
                            direction += 180;
                        }
                    }

                    weatherSeries.currents.timeseries.timestamps.push(new Date(spot.timestamp).getTime());
                    weatherSeries.currents.timeseries.direction!.push(direction);
                    weatherSeries.currents.timeseries.values.push(parseFloat(mpsToKnots(spot.weather.Currents.value).toFixed(1)));
                }
            }
        }

        updatedWeatherData.wind50M.push(weatherSeries.wind50M);
        updatedWeatherData.swell.push(weatherSeries.swell);
        updatedWeatherData.wave.push(weatherSeries.wave);
        updatedWeatherData.currents.push(weatherSeries.currents);
    }
    
    return updatedWeatherData;
}

export function fixupInvalidPosValues(p: VesselPositionData) {
    if (p.sog && p.sog >= 102.3) {
        p.sog = null;
    }

    if (p.heading && p.heading >= 511) {
        p.heading = null;
    }

    if (p.course && p.course >= 511) {
        p.course = null;
    }
}

export function toTitleCase(str: string) {
    return str.toLowerCase().replace(/(?:^|\s)\w/g, function(match) {
        return match.toUpperCase();
    });
}

//function to extract user information from JWT token
export function getUserInfoFromToken(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}
